import type { ReactElement, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { Card, CardContent } from '../primitives/Card.js';

export interface PageStatTileProps {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
}

/**
 * Tile de KPI canônico. Visualmente carrega 2 elementos de identidade:
 *   - **Ícone com fundo gold** (`bg-accent/15 text-accent` + ring sutil) —
 *     toque pontual de marca em cada KPI; coerente com eyebrow do
 *     PageIntroCard.
 *   - **Hover lift** (`hover:-translate-y-0.5 hover:shadow-lg`) —
 *     micro-interação que dá profundidade sem ser barulhento. Respeita
 *     `prefers-reduced-motion` via tokens de duração.
 *
 * O **valor** continua em `text-foreground` (preto/grafite) — não usar
 * gold em valores numéricos porque não há ranking semântico para a cor
 * (todo número de KPI é igualmente importante; gold puxaria atenção
 * para um arbitrário).
 */
export function PageStatTile({ label, value, hint, icon: Icon }: PageStatTileProps): ReactElement {
  return (
    <Card className="border-border bg-card shadow-md shadow-foreground/5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-foreground/10 dark:shadow-xl dark:shadow-black/30 dark:hover:shadow-2xl dark:hover:shadow-black/50">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          {Icon ? (
            <span className="rounded-full bg-accent/15 p-2 text-accent ring-1 ring-accent/20">
              <Icon className="size-4" aria-hidden="true" />
            </span>
          ) : null}
        </div>
        <div className="space-y-2">
          <p className="text-3xl font-semibold tracking-tight sm:text-4xl">{value}</p>
          {hint ? <p className="text-sm leading-6 text-muted-foreground">{hint}</p> : null}
        </div>
      </CardContent>
    </Card>
  );
}
